"use client";
import { useEffect } from "react";
import { consumeSkip, isOwnEntry, pushBackEntry, touchEnabled } from "@/lib/backStack";

/**
 * زرّ الرجوع في الهاتف يغلق النافذة المنبثقة العليا ولا يغادر الصفحة، والرجوع التالي يعود إلى الصفحة
 * السابقة (طلب المالك 2026-10-08) — لكلّ النوافذ (71 بخلفيّة `bg-th-overlay` + `ui/dialog`) ودرج القائمة
 * الجانبيّة، بلا تعديل أيّ صفحة. على شاشات اللمس فقط (لا يشمل الكمبيوتر). مُركَّب مرّة في `app/layout.tsx`.
 *
 * - النافذة = عنصر `.fixed.inset-0` بخلفيّة `bg-th-overlay` (عليه أو على ابن مباشر `absolute`)، أو `role=dialog`.
 *   خارجها عمدًا: المشغّلات بملء الشاشة (خلفيّتها سوداء)، قوائم الالتقاط الشفّافة، `data-back-ignore`
 *   (نافذة الاختيار تدير سجلّها بنفسها).
 * - ظهور نافذة ⇒ سجلّ تاريخ واحد (`pushBackEntry` — يحمل حالة Next، وإلّا أعاد الموجّه تحميل الصفحة).
 * - الرجوع ⇒ تُغلق النافذة العليا بطريقتها: زرّ «إغلاق» فيها ← Esc ← الضغط على خلفيّتها. إن بقيت
 *   (مشغولة/تأكيد) يُعاد سجلّها فلا يغادر المستعمل الصفحة.
 * - **أُغلقت بزرّها (× · حفظ · رابط) ⇒ لا شيء** — يبقى سجلّها، فأوّل رجوع بعدها بلا أثر ظاهر (الخيار أ للمالك:
 *   ضغطة زائدة مقابل الأمان). كان سجلّها يُسحب بـ`history.back()` فألغى السحبُ مرّتين تنقّلًا بدأ للتوّ
 *   (روابط الدرج على الهاتف — HANDOFF §6.6). **لا تُعِد السحب.**
 * - وصل المتصفّح إلى سجلّ متبقٍّ لا نافذة له (رجوع من صفحة أخرى) ⇒ يُتجاوز برجوع واحد — ردًّا على
 *   رجوع المستعمل نفسه، فلا تنقّل يُلغى.
 */

const OVERLAY = ".fixed.inset-0";

function isModal(el: Element): boolean {
  if (!(el instanceof HTMLElement) || el.hasAttribute("data-back-ignore")) return false;
  if (el.closest("[data-back-ignore]")) return false;
  const cls = el.className || "";
  if (typeof cls !== "string") return false;
  if (/\bbg-th-overlay/.test(cls) || el.getAttribute("role") === "dialog") return true;
  return Array.from(el.children).some((c) => c.classList.contains("absolute") && /\bbg-th-overlay/.test(c.className as string));
}

function visible(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  // بالنمط المحسوب صعودًا (لا getClientRects — يعمل في jsdom أيضًا): مخفيّة إن كانت هي أو أحد آبائها display:none
  for (let n: HTMLElement | null = el; n; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (cs.display === "none" || cs.visibility === "hidden") return false;
  }
  return true;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function BackToCloseManager({ force }: { force?: boolean } = {}) {
  useEffect(() => {
    if (!touchEnabled(force)) return;
    let open: HTMLElement[] = []; // بترتيب الظهور — الأخيرة هي العليا

    const scan = () => {
      const now = Array.from(document.querySelectorAll(OVERLAY)).filter(
        (e): e is HTMLElement => isModal(e) && visible(e as HTMLElement),
      );
      for (const el of now) {
        if (!open.includes(el)) {
          open.push(el);
          pushBackEntry("modal");
        }
      }
      open = open.filter((el) => now.includes(el)); // أُغلقت بزرّها: لا سحب (انظر أعلاه)
    };

    const tryClose = async (top: HTMLElement) => {
      const closed = () => !top.isConnected || !visible(top);
      const btn = top.querySelector<HTMLButtonElement>('button[aria-label="إغلاق"]:not(:disabled), button[title="إغلاق"]:not(:disabled)');
      if (btn) { btn.click(); await wait(60); if (closed()) return true; }
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
      await wait(60); if (closed()) return true;
      const backdrop = (Array.from(top.children).find((c) => c.classList.contains("absolute")) as HTMLElement) || top;
      backdrop.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      backdrop.dispatchEvent(new MouseEvent("click", { bubbles: true }));
      await wait(60);
      return closed();
    };

    const onPop = async () => {
      if (consumeSkip()) return; // سحبُ نافذة الاختيار لسجلّها
      if (document.querySelector("[data-testid='touch-select-overlay']")) return; // نافذة الاختيار تتولّاه
      scan();
      const top = open[open.length - 1];
      if (!top) {
        if (isOwnEntry()) history.back(); // سجلّ متبقٍّ بلا نافذة ⇒ تجاوزه
        return;
      }
      const ok = await tryClose(top);
      open = open.filter((el) => el !== top);
      if (!ok) {
        open.push(top);
        pushBackEntry("modal"); // لم تُغلق (مشغولة/تأكيد) ⇒ ابقَ في الصفحة
      }
    };

    const mo = new MutationObserver(() => scan());
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style", "hidden"] });
    window.addEventListener("popstate", onPop);
    scan();
    return () => {
      mo.disconnect();
      window.removeEventListener("popstate", onPop);
    };
  }, [force]);

  return null;
}
